import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { AuthState, RegisterData } from '../types/auth';
import { authApi } from '../services/api';
import { useTranslation } from 'react-i18next';

interface AuthContextType extends AuthState {
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: localStorage.getItem('token'),
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const { t } = useTranslation();

  const mapAuthError = (error: any, fallbackKey: string) => {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const detailText = typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? JSON.stringify(detail)
        : String(detail || '');

    if (
      status === 400 &&
      /email_already_registered|already exists/i.test(detailText)
    ) {
      return t('auth.email_already_registered');
    }
    if (status === 400 && /username_already_exists/i.test(detailText)) {
      return t('auth.email_already_registered');
    }
    if (status === 422) {
      if (/accepted_terms/i.test(detailText)) {
        return t('auth.accept_terms_required');
      }
      if (/email/i.test(detailText)) {
        return t('auth.invalid_email');
      }
    }
    if (status === 429) {
      return t('auth.too_many_attempts');
    }
    if (status === 400 && /captcha/i.test(detailText)) {
      return t('auth.captcha_retry');
    }
    if (status === 401) {
      return t('auth.invalid_credentials');
    }
    return t(fallbackKey);
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('password');
    localStorage.removeItem('user_type');
    localStorage.removeItem('user_id');
    localStorage.removeItem('current_email');
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }, []);

  const refreshToken = useCallback(async () => {
    try {
      if (!state.user?.email) {
        throw new Error('User email not found');
      }
      
      const password = localStorage.getItem('password') || '';
      if (!password) {
        throw new Error('Password not found in local storage');
      }
      
      const authResponse = await authApi.login(state.user.email, password);
      
      localStorage.setItem('token', authResponse.access_token);
      setState(prev => ({
        ...prev,
        token: authResponse.access_token,
      }));
    } catch (error) {
      console.error('Error refreshing token:', error);
      logout();
    }
  }, [state.user?.email, logout]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token && !state.user) {
        try {
          const user = await authApi.getCurrentUser();
          // Вземаме имейла от localStorage, ако го има (за да работи forceCorrectUserType и при рефреш)
          const emailForType = localStorage.getItem('current_email') || user.email;
          const correctedUser = forceCorrectUserType(user, emailForType);
          setState(prev => ({
            ...prev,
            user: correctedUser,
            isAuthenticated: true,
            isLoading: false,
          }));

          // Проверяваме дали токенът е валиден
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            const expirationTime = payload.exp * 1000;
            
            // Ако токенът ще изтече след по-малко от 5 минути, опресняваме го
            if (Date.now() >= expirationTime - 5 * 60 * 1000) {
              await refreshToken();
            }
          }
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('password');
          setState(prev => ({
            ...prev,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: t('auth.session_expired'),
          }));
        }
      } else if (!token) {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    };

    initAuth();
  }, [t, refreshToken, state.user]);

  // Функция за форсирано задаване на потребителския тип според имейла
  const forceCorrectUserType = (userData: any, email: string) => {
    if (!userData) return userData;
    
    // Копираме данните, за да не модифицираме оригиналния обект
    const updatedUserData = { ...userData };
    
    // Проверяваме дали имейлът е на професионалист (mitat1976@abv.bg)
    if (email === 'mitat1976@abv.bg') {
      updatedUserData.user_type = 'professional';
      localStorage.setItem('user_type', 'professional');
    } else {
      // Проверяваме дали има запазен тип потребител
      const storedUserType = localStorage.getItem('user_type');
      if (storedUserType && (storedUserType === 'client' || storedUserType === 'professional')) {
        updatedUserData.user_type = storedUserType;
      } else {
        updatedUserData.user_type = 'client';
        localStorage.setItem('user_type', 'client');
      }
    }
    
    // Съхраняваме ID на потребителя за по-лесен достъп
    if (updatedUserData.id) {
      localStorage.setItem('user_id', updatedUserData.id.toString());
    }
    
    return updatedUserData;
  };

  const login = async (email: string, password: string, captchaToken?: string) => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await authApi.login(email, password, captchaToken);
      
      // Запазваме токена и паролата за опресняване на токена
      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        localStorage.setItem('password', password);
        localStorage.setItem('current_email', email);
        
        // Предварително определяме типа потребител според имейла
        const userType = email === 'mitat1976@abv.bg' ? 'professional' : 'client';
        localStorage.setItem('user_type', userType);
        
        setState(prev => ({
          ...prev,
          token: response.access_token,
        }));
      }
      
      // Зареждаме потребителската информация
      let user = await authApi.getCurrentUser();
      
      // Прилагаме форсирана корекция на потребителския тип
      user = forceCorrectUserType(user, email);
      setState(prev => ({
        ...prev,
        user,
        isAuthenticated: true,
        isLoading: false,
      }));
    } catch (error) {
      console.error('Login error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: mapAuthError(error, 'auth.invalid_credentials'),
      }));
      throw error;
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const email = localStorage.getItem('current_email') || state.user?.email;
      let user = await authApi.getCurrentUser();
      if (email) {
        user = forceCorrectUserType(user, email);
      }
      setState(prev => ({ ...prev, user }));
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  }, [state.user?.email]);

  const register = async (data: RegisterData) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      // /auth/signup вече връща access_token директно - не правим повторна
      // /login заявка, защото CAPTCHA token-ът е еднократен и вече е
      // консумиран от самата регистрация (втора употреба винаги би фейлнала).
      const { user, access_token } = await authApi.register(data);

      localStorage.setItem('token', access_token);
      localStorage.setItem('password', data.password); // Запазваме паролата за опресняване
      setState(prev => ({
        ...prev,
        user,
        token: access_token,
        isAuthenticated: true,
        isLoading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: mapAuthError(error, 'auth.registration_failed'),
      }));
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout, refreshToken, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 